import { useCallback, useEffect, useState } from 'react';
import { AsyncThunk } from '@reduxjs/toolkit';
import { useInView } from 'react-intersection-observer';

import { Pager, PaginationParams } from 'models';

import { useAppDispatch } from './useAppDispatch';

import { RequestStatus } from 'utils/constants';

const STATUS = RequestStatus.PENDING.toUpperCase();
const PAGE_SIZE = 40;

export const useGetItems = ({
	callback,
	callbackMore,
	pager,
	isLoading,
	isSent,
}: {
	callback: AsyncThunk<any, PaginationParams | undefined, {}>;
	callbackMore: AsyncThunk<any, PaginationParams | undefined, {}>;
	pager: Pager;
	isLoading: boolean;
	isSent?: boolean;
}) => {
	const { ref, inView } = useInView({
		threshold: 1,
	});

	const dispatch = useAppDispatch();

	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const onGetItems = useCallback(
		(data: PaginationParams, callback: AsyncThunk<any, PaginationParams | undefined, {}>) => {
			dispatch(callback(data))
				.unwrap()
				.finally(() => {
					setIsLoadingMore(false);
				});
		},
		[dispatch],
	);

	const status = isSent ? undefined : STATUS;

	useEffect(() => {
		if (inView && !isLoadingMore && pager.currentPage < pager.pageCount) {
			setIsLoadingMore(true);
		}
	}, [inView, isLoadingMore, pager]);

	useEffect(() => {
		if (isLoadingMore && pager.currentPage < pager.pageCount && !isLoading) {
			onGetItems(
				{
					pageSize: PAGE_SIZE,
					offset: pager.offset + PAGE_SIZE,
					status,
				},
				callbackMore,
			);
		}
	}, [isLoading, isLoadingMore, onGetItems, pager.currentPage, pager.offset, pager.pageCount]);

	useEffect(() => {
		onGetItems({ pageSize: PAGE_SIZE, offset: 0, status }, callback);
	}, [onGetItems]);

	return { ref, isLoadingMore };
};
